import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtEUR, formatApiError, API_BASE } from "../lib/api";
import { useSearchParams } from "react-router-dom";
import { BookingLiveGps } from "../components/gps/BookingLiveGps";
import BookingModal from "../components/BookingModal";
import { StatusBadge } from "../components/StatusBadge";
import { ExecutorSection, resolveExecutor } from "../components/booking/ExecutorSection";
import { PaymentMethodBadge, PaymentFilter, payGroup } from "../components/booking/PaymentMethod";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import {
  Plus, Edit2, Trash2, Download, FileText, ArrowLeftRight, AlertTriangle,
  Search, X, Calendar, Users, TrendingUp, Car, Copy, Eye, MapPin, Phone,
  Mail, Plane, Building2, Filter as FilterIcon, Clock, User as UserIcon, Repeat,
  Send, Inbox, Ban, Check, CheckCircle2, RefreshCw, Link2, MessageCircle, AlertCircle, Flag, Briefcase
} from "lucide-react";
import { DayCalendar } from "../components/booking/DayCalendar";
import StrianoLogo from "../components/StrianoLogo";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const STATUSES = ["Tutti", "Preventivo", "Confermata", "Assegnata", "In corso", "Completata", "Cancellata", "No show"];

function pickIcon(text) {
  if (!text) return MapPin;
  const t = text.toLowerCase();
  if (/aeroporto|airport/.test(t)) return Plane;
  if (/hotel|resort|albergo/.test(t)) return Building2;
  return MapPin;
}
const initials = (a = "", b = "") => `${(a[0] || "").toUpperCase()}${(b[0] || "").toUpperCase()}`;
const fmtDate = (iso) => { if (!iso) return "-"; const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; };
const romeToday = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });
// (iter 34) Formatter per la data/ora di visualizzazione driver
const fmtViewed = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const gg = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${gg}/${mm}/${yy} ${hh}:${mi}`;
  } catch { return iso; }
};

export default function Prenotazioni() {
  const { user } = useAuth();
  const isDriver = user?.role === "autista";
  const [rows, setRows] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const _qs = new URLSearchParams(window.location.search);
  // Default: giornata odierna (Europe/Rome); ?date ha priorità, ?open mantiene l'elenco completo per trovare il servizio
  const _initDay = _qs.get("date") || (_qs.get("open") ? "" : romeToday());
  const autoDayRef = useRef(_qs.get("date") || _qs.get("open") ? null : _initDay);
  const [status, setStatus] = useState(STATUSES.includes(_qs.get("status")) ? _qs.get("status") : "Tutti");
  const [payF, setPayF] = useState("all");
  const [dateFrom, setDateFrom] = useState(_initDay);
  const [dateTo, setDateTo] = useState(_initDay);
  const [driverId, setDriverId] = useState("all");
  const [customerId, setCustomerId] = useState("all");
  const [vehicleId, setVehicleId] = useState("all");
  const [query, setQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [returnFromId, setReturnFromId] = useState(null);
  const [detailBooking, setDetailBooking] = useState(null);
  const [exchangeBooking, setExchangeBooking] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [operatorsDir, setOperatorsDir] = useState([]);
  const [exchangeTargetOperator, setExchangeTargetOperator] = useState("");
  const [exchangeNotes, setExchangeNotes] = useState("");
  const [exchangePartnerId, setExchangePartnerId] = useState("none");
  // Mappa scambi: booking_id -> exchange attivo (uno solo attivo per prenotazione)
  const [exchangesMap, setExchangesMap] = useState({});
  // Scambi ricevuti già accettati (prenotazioni che devo eseguire come fornitore)
  const [receivedServices, setReceivedServices] = useState([]);
  const [meOpId, setMeOpId] = useState(null);
  // Modal "Invia tramite WhatsApp" (manuale, nessun invio automatico)
  const [waBooking, setWaBooking] = useState(null);
  const [shiftsMap, setShiftsMap] = useState({});
  const [stats, setStats] = useState({ today_count: 0, revenue_month: 0, vehicles_busy: 0, vehicles_total: 0, incassato: 0 });

  const load = async () => {
    const params = {};
    if (status !== "Tutti") params.status = status;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (driverId !== "all") params.driver_id = driverId;
    if (customerId !== "all") params.customer_id = customerId;
    if (vehicleId !== "all") params.vehicle_id = vehicleId;
    const [r, exSent, exInbox, me] = await Promise.all([
      api.get("/bookings", { params }),
      api.get("/exchanges", { params: { box: "sent" } }).catch(() => ({ data: [] })),
      api.get("/exchanges", { params: { box: "inbox" } }).catch(() => ({ data: [] })),
      api.get("/auth/me").catch(() => ({ data: {} })),
    ]);
    setRows(r.data);
    // Mappa degli scambi attivi (status pending/accepted) sulla prenotazione originale (mittente lato)
    const map = {};
    (exSent.data || []).forEach(e => {
      if (!e.booking_id) return;
      if (["pending", "accepted"].includes(e.status)) map[e.booking_id] = e;
      // Se la prenotazione non ha ancora uno scambio attivo, memorizziamo anche gli storici per visualizzazione
      else if (!map[e.booking_id]) map[e.booking_id] = e;
    });
    setExchangesMap(map);
    // Servizi ricevuti e accettati (fornitore / destinatario): compaiono anche in Prenotazioni
    setReceivedServices((exInbox.data || []).filter(e => ["accepted", "completed"].includes(e.status)));
    setMeOpId(me.data?.operator_id || null);
  };

  useEffect(() => {
    Promise.allSettled([
      api.get("/drivers"), api.get("/vehicles"), api.get("/customers"),
      api.get("/suppliers"), api.get("/dashboard/summary"),
      api.get("/operators/directory").catch(() => ({ data: [] })),
    ]).then(([d,v,c,s,st,od]) => {
      if (d.status === "fulfilled") setDrivers(d.value.data);
      if (v.status === "fulfilled") setVehicles(v.value.data);
      if (c.status === "fulfilled") setCustomers(c.value.data);
      if (s.status === "fulfilled") setSuppliers(s.value.data);
      if (st.status === "fulfilled") setStats(st.value.data);
      if (od.status === "fulfilled") setOperatorsDir(od.value.data || []);
    });
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, dateFrom, dateTo, driverId, customerId, vehicleId]);

  // Dopo mezzanotte passa al nuovo giorno solo se l'intervallo è ancora quello automatico
  useEffect(() => {
    const t = setInterval(() => {
      const d = romeToday(), auto = autoDayRef.current;
      if (auto && d !== auto && dateFrom === auto && dateTo === auto) { autoDayRef.current = d; setDateFrom(d); setDateTo(d); }
    }, 60000);
    return () => clearInterval(t);
  }, [dateFrom, dateTo]);

  // (AI Agent) Auto-apre il servizio passato via ?open=<booking_id> (+ ?edit=1 per aprire in modifica)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const id = searchParams.get("open");
    if (!id || rows.length === 0) return;
    const b = rows.find((x) => x.id === id);
    if (b) {
      const wantEdit = searchParams.get("edit") === "1";
      if (wantEdit) {
        setEditing(b);
        setShowModal(true);
      } else {
        setDetailBooking(b);
      }
      searchParams.delete("open");
      searchParams.delete("edit");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // ESC per chiudere pannello dettagli
  useEffect(() => {
    if (!detailBooking) return;
    const onKey = (e) => { if (e.key === "Escape") setDetailBooking(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [detailBooking]);

  useEffect(() => {
    const uniqMonths = new Set();
    rows.forEach(b => { if (b.date && b.driver_id) uniqMonths.add(b.date.slice(0, 7)); });
    if (!uniqMonths.size) { setShiftsMap({}); return; }
    Promise.all(Array.from(uniqMonths).map(ym => {
      const [y, m] = ym.split("-");
      return api.get("/shifts", { params: { year: Number(y), month: Number(m) } }).catch(() => ({ data: [] }));
    })).then(results => {
      const map = {};
      results.forEach(r => (r.data || []).forEach(s => { map[`${s.date}|${s.driver_id}`] = s.shift_type; }));
      setShiftsMap(map);
    });
  }, [rows]);

  const driver = (id) => drivers.find(x => x.id === id);
  const vehicle = (id) => vehicles.find(x => x.id === id);
  const customer = (id) => customers.find(x => x.id === id);

  const searchedRows = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.toLowerCase();
    return rows.filter(b => {
      const drv = driver(b.driver_id); const veh = vehicle(b.vehicle_id); const cst = customer(b.customer_id);
      return [b.booking_number, b.passenger_name, b.pickup, b.destination, b.notes,
              cst?.name, drv ? `${drv.first_name} ${drv.last_name}` : "",
              veh ? `${veh.brand} ${veh.model} ${veh.plate}` : ""]
        .some(x => x && String(x).toLowerCase().includes(q));
    });
  }, [rows, query, drivers, vehicles, customers]);
  const payCounts = useMemo(() => {
    const c = { all: searchedRows.length };
    searchedRows.forEach(b => { const g = payGroup(b.payment_method); c[g] = (c[g] || 0) + 1; });
    return c;
  }, [searchedRows]);
  const filteredRows = useMemo(() => {
    const r = payF === "all" ? searchedRows : searchedRows.filter(b => payGroup(b.payment_method) === payF);
    return dateFrom && dateFrom === dateTo ? [...r].sort((a, b) => String(a.time || "").localeCompare(String(b.time || ""))) : r;
  }, [searchedRows, payF, dateFrom, dateTo]);
  const emptyMsg = dateFrom && dateFrom === dateTo && dateFrom === romeToday() ? "Nessun servizio programmato per oggi" : "Nessuna prenotazione";

  const todayIso = romeToday();
  const todayBookings = rows.filter(b => b.date === todayIso && b.status !== "Cancellata");
  const todayRevenue = todayBookings.reduce((a, b) => a + Number(b.price || 0), 0);
  const uniqueCustomers = new Set(rows.filter(b => b.customer_id).map(b => b.customer_id)).size;

  const clearFilters = () => { const d = romeToday(); autoDayRef.current = d; setStatus("Tutti"); setDateFrom(d); setDateTo(d); setDriverId("all"); setCustomerId("all"); setVehicleId("all"); setQuery(""); setPayF("all"); };
  const remove = async (id) => {
    if (!confirm("Eliminare la prenotazione?")) return;
    try { await api.delete(`/bookings/${id}`); toast.success("Eliminata"); setDetailBooking(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  // Costruisce una copia "pulita" della prenotazione da riutilizzare come base per Duplica / Crea Ritorno.
  // Rimuove: id, numero, data/ora, stato, pagamento, incassi/anticipi, spese, exchange, riferimenti a incassi già registrati.
  const cloneBookingBase = (b) => {
    const {
      id, booking_number, created_at, exchange_id, related_booking_id,
      date, time, status, payment_status, payment_method,
      driver_collected, driver_advanced, driver_expenses, expense_items,
      pickup_lat, pickup_lon, destination_lat, destination_lon, distance_km, duration_min,
      ...rest
    } = b;
    return {
      ...rest,
      status: "Preventivo",
      payment_status: "Da incassare",
      payment_method: "",
      driver_collected: 0,
      driver_advanced: 0,
      driver_expenses: 0,
      expense_items: [],
    };
  };

  const duplicate = (b) => {
    const base = cloneBookingBase(b);
    setEditing(base);
    setReturnFromId(null);
    setDetailBooking(null);
    setShowModal(true);
  };

  const createReturn = (b) => {
    const base = cloneBookingBase(b);
    setEditing({
      ...base,
      pickup: b.destination || "",
      destination: b.pickup || "",
      related_booking_id: b.id,
    });
    setReturnFromId(b.id);
    setDetailBooking(null);
    setShowModal(true);
  };
  const exportCSV = () => {
    const header = ["N°","Data","Ora","Stato","Cliente","Passeggero","Servizio","Partenza","Destinazione","Autista","Veicolo","Prezzo","Costo Autista","Margine"];
    const lines = [header.join(";")];
    filteredRows.forEach(b => {
      const dn = driver(b.driver_id); const vn = vehicle(b.vehicle_id); const cn = customer(b.customer_id);
      lines.push([b.booking_number, b.date, b.time, b.status, cn?.name, b.passenger_name, b.service_type, b.pickup, b.destination, dn ? `${dn.first_name} ${dn.last_name}` : "", vn ? `${vn.brand} ${vn.model}` : "", b.price, b.driver_cost, b.margin].map(x => (x??"").toString().replace(/;/g,",")).join(";"));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `prenotazioni_${new Date().toISOString().slice(0,10)}.csv`; a.click();
  };

  const activeVehiclesPct = stats.vehicles_total > 0 ? Math.round((stats.vehicles_busy / stats.vehicles_total) * 100) : 0;

  return (
    <div className="pren-luxe space-y-5 pb-16" data-testid="prenotazioni-page">
      {/* HEADER */}
      <div className="pl-panel pl-hero p-5">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <StrianoLogo variant="compact" size={44} data-testid="prenotazioni-logo"/>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#D6A62A] font-semibold">Striano NCC</div>
                <h1 className="font-display text-3xl sm:text-4xl font-semibold text-[#F3EDE0] tracking-tight">Prenotazioni</h1>
              </div>
            </div>
            <div className="text-sm text-[#A8A39A] mt-1.5 ml-14">Gestisci tutte le prenotazioni della tua flotta</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"/>
              <Input data-testid="global-search" placeholder="Cerca prenotazioni, clienti, autisti…" value={query} onChange={e=>setQuery(e.target.value)} className="bg-slate-950 border-slate-800 pl-9 w-72"/>
            </div>
            <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300 h-10" onClick={exportCSV} data-testid="export-btn"><Download size={16}/> Esporta</Button>
            {!isDriver && <Button data-testid="new-booking-btn" onClick={()=>{setEditing(null); setShowModal(true);}} className="btn-gold-primary font-bold h-10 rounded-xl"><Plus size={16} strokeWidth={3}/> Nuova Prenotazione</Button>}
          </div>
        </div>
      </div>

      {/* STAT CARDS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Calendar} label="Prenotazioni oggi" value={todayBookings.length} hint={`${rows.length} totali`} tone="amber"/>
        <StatCard icon={TrendingUp} label="Fatturato oggi" value={fmtEUR(todayRevenue)} hint={`${fmtEUR(stats.revenue_month)} mese`} tone="emerald"/>
        <StatCard icon={Users} label="Clienti serviti" value={uniqueCustomers} hint={`${customers.length} in anagrafica`} tone="sky"/>
        <StatCard icon={Car} label="Veicoli in servizio" value={`${stats.vehicles_busy}/${stats.vehicles_total || 0}`} pct={activeVehiclesPct} tone="purple"/>
      </div>

      <DayCalendar dateFrom={dateFrom} dateTo={dateTo} today={todayIso} count={filteredRows.length}
        onPick={(d)=>{ setDateFrom(d); setDateTo(d); }} onRefresh={load}/>

      {/* FILTERS */}
      <div className="pl-panel p-4 no-print">
        <div className="flex items-center gap-2 mb-3">
          <FilterIcon size={14} className="text-[#D6A62A]"/>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num font-bold">Filtri</div>
          <button onClick={clearFilters} className="ml-auto text-xs text-amber-400 hover:text-amber-300 font-bold flex items-center gap-1" data-testid="clear-filters-btn"><X size={12}/> Pulisci filtri</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <FilterField label="Da"><Input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} className="bg-slate-950 border-slate-800" data-testid="filter-date-from"/></FilterField>
          <FilterField label="A"><Input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} className="bg-slate-950 border-slate-800" data-testid="filter-date-to"/></FilterField>
          <FilterField label="Cliente"><Select value={customerId} onValueChange={setCustomerId}><SelectTrigger className="bg-slate-950 border-slate-800"><SelectValue/></SelectTrigger><SelectContent className="bg-slate-950 border-slate-800"><SelectItem value="all">Tutti</SelectItem>{customers.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></FilterField>
          <FilterField label="Autista"><Select value={driverId} onValueChange={setDriverId}><SelectTrigger className="bg-slate-950 border-slate-800"><SelectValue/></SelectTrigger><SelectContent className="bg-slate-950 border-slate-800"><SelectItem value="all">Tutti</SelectItem>{drivers.map(d=><SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}</SelectContent></Select></FilterField>
          <FilterField label="Veicolo"><Select value={vehicleId} onValueChange={setVehicleId}><SelectTrigger className="bg-slate-950 border-slate-800"><SelectValue/></SelectTrigger><SelectContent className="bg-slate-950 border-slate-800"><SelectItem value="all">Tutti</SelectItem>{vehicles.map(v=><SelectItem key={v.id} value={v.id}>{v.brand} {v.model}</SelectItem>)}</SelectContent></Select></FilterField>
          <FilterField label="Stato"><Select value={status} onValueChange={setStatus}><SelectTrigger className="bg-slate-950 border-slate-800"><SelectValue/></SelectTrigger><SelectContent className="bg-slate-950 border-slate-800">{STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></FilterField>
        </div>
      </div>

      {/* SERVIZI RICEVUTI (SCAMBI ACCETTATI) */}
      {!isDriver && receivedServices.length > 0 && (
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 overflow-hidden" data-testid="received-services-section">
          <div className="px-5 py-3 border-b border-emerald-500/20 flex items-center gap-2">
            <Inbox size={16} className="text-emerald-400"/>
            <div className="text-[11px] uppercase tracking-widest text-emerald-300 font-mono-num font-bold">Servizi Affidati a Me da Fornitori</div>
            <span className="ml-auto text-[10px] font-mono-num text-emerald-400 bg-emerald-500/15 border border-emerald-500/30 rounded px-2 py-0.5">{receivedServices.length} servizi</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
                <tr>
                  <th className="p-3 text-left">Data / Ora</th>
                  <th className="text-left">Mittente</th>
                  <th className="text-left">Cliente</th>
                  <th className="text-left">Tratta</th>
                  <th className="text-left">Autista Assegnato</th>
                  <th className="text-left">Stato</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {receivedServices.map(ex => {
                  const drv = drivers.find(d => d.id === ex.to_driver_id);
                  const openReceivedBooking = async () => {
                    if (!ex.booking_id) { toast.error("Prenotazione originale non trovata"); return; }
                    try {
                      const r = await api.get(`/bookings/${ex.booking_id}`);
                      setDetailBooking({ ...r.data, _exchange_context: ex });
                    } catch (e) { toast.error(formatApiError(e)); }
                  };
                  return (
                    <tr key={ex.id} className="hover:bg-emerald-500/10 cursor-pointer transition-colors" onClick={openReceivedBooking} data-testid={`received-row-${ex.exchange_number}`}>
                      <td className="p-3">
                        <div className="text-white font-bold">{fmtDate(ex.date)}</div>
                        <div className="text-xs text-amber-400 font-mono-num">{ex.time || "-"}</div>
                        <div className="text-[10px] text-emerald-400 font-mono-num mt-0.5">{ex.exchange_number}</div>
                      </td>
                      <td>
                        <div className="flex items-center gap-1.5 text-xs">
                          <Send size={11} className="text-purple-400"/>
                          <span className="text-white font-bold truncate max-w-[150px]">{ex.from_operator_name || "-"}</span>
                        </div>
                      </td>
                      <td className="text-slate-200 text-sm truncate max-w-[160px]">{ex.customer || ex.passenger_name || "-"}</td>
                      <td className="text-xs">
                        <div className="flex items-center gap-1.5 text-slate-300"><MapPin size={11} className="text-emerald-400 flex-shrink-0"/><span className="truncate max-w-[180px]">{ex.pickup || "-"}</span></div>
                        <div className="flex items-center gap-1.5 text-slate-400 mt-0.5"><MapPin size={11} className="text-rose-400 flex-shrink-0"/><span className="truncate max-w-[180px]">{ex.destination || "-"}</span></div>
                      </td>
                      <td className="text-slate-300 text-sm">
                        {drv ? `${drv.first_name} ${drv.last_name}` : <span className="text-slate-500 italic text-xs">Da assegnare</span>}
                      </td>
                      <td>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${
                          ex.status === "accepted" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-sky-500/15 text-sky-300 border-sky-500/40"
                        }`}>
                          <CheckCircle2 size={10}/> {ex.status === "accepted" ? "Affidato" : "Completato"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* FILTRO METODO DI PAGAMENTO CLIENTE */}
      <PaymentFilter value={payF} onChange={setPayF} counts={payCounts}/>

      {/* TABLE */}
      <div className="pl-panel pl-list overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
              <tr>
                <th className="p-3 text-left">Data / Ora</th>
                <th className="text-left">Cliente</th>
                <th className="text-left">Tratta</th>
                <th className="text-left">Autista / Veicolo</th>
                <th className="text-left">Stato</th>
                <th className="text-right">Prezzo</th>
                <th className="text-right pr-3">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {filteredRows.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-slate-500" data-testid="bookings-empty">{emptyMsg}</td></tr>}
              {filteredRows.map(b => {
                const drv = driver(b.driver_id); const veh = vehicle(b.vehicle_id); const cst = customer(b.customer_id);
                const shiftType = b.driver_id && shiftsMap[`${b.date}|${b.driver_id}`];
                const PIcon = pickIcon(b.pickup); const DIcon = pickIcon(b.destination);
                return (
                  <tr key={b.id} className={`${b.driver_id && b.driver_viewed ? "bg-emerald-500/5 hover:bg-emerald-500/10 border-l-2 border-emerald-500/60" : "hover:bg-slate-900/50"} cursor-pointer transition-colors`} onClick={()=>setDetailBooking(b)} data-testid={`booking-row-${b.id}`}>
                    <td className="p-3">
                      <div className="text-white font-bold">{fmtDate(b.date)}</div>
                      <div className="text-xs text-amber-400 font-mono-num">{b.time}</div>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <div className="text-[10px] text-slate-500 font-mono-num">{b.booking_number}</div>
                        {b._received_via_exchange && (
                          <span
                            data-testid={`received-exchange-badge-${b.id}`}
                            title="Servizio ricevuto da scambio"
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-mono-num font-bold text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded px-1.5 py-0.5"
                          >
                            <ArrowLeftRight size={9}/> Scambio
                          </span>
                        )}
                        {b.exchange_driver_name && (
                          <span
                            data-testid={`exchange-driver-chip-${b.id}`}
                            title={`Driver: ${b.exchange_driver_name}${b.exchange_vehicle_name ? ` · ${b.exchange_vehicle_name}` : ""}${b.exchange_vehicle_plate ? ` · ${b.exchange_vehicle_plate}` : ""}`}
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-mono-num font-bold text-emerald-300 bg-emerald-500/10 border border-emerald-500/40 rounded px-1.5 py-0.5"
                          >
                            {b.exchange_driver_name.split(" ")[0]}{b.exchange_vehicle_plate ? ` · ${b.exchange_vehicle_plate}` : ""}
                          </span>
                        )}
                        {b.driver_id && !isDriver && (
                          b.driver_viewed ? (
                            <span
                              data-testid={`driver-viewed-badge-${b.id}`}
                              title={`Visualizzato dal driver${b.driver_viewed_at ? ` · ${fmtViewed(b.driver_viewed_at)}` : ""}`}
                              className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-mono-num font-bold text-emerald-300 bg-emerald-500/15 border border-emerald-500/50 rounded px-1.5 py-0.5"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"/> Visualizzato
                            </span>
                          ) : (
                            <span
                              data-testid={`driver-not-viewed-badge-${b.id}`}
                              title="Il driver non ha ancora aperto il servizio"
                              className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-mono-num font-bold text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded px-1.5 py-0.5"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"/> Da visualizzare
                            </span>
                          )
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="text-white font-bold">{cst?.name || b.passenger_name || "-"}</div>
                      {cst?.type && <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{cst.type}</div>}
                    </td>
                    <td className="text-xs">
                      <div className="flex items-center gap-1.5 text-slate-300"><PIcon size={11} className="text-emerald-400 flex-shrink-0"/><span className="truncate max-w-[200px]">{b.pickup || "-"}</span></div>
                      <div className="flex items-center gap-1.5 text-slate-400 mt-0.5"><DIcon size={11} className="text-rose-400 flex-shrink-0"/><span className="truncate max-w-[200px]">{b.destination || "-"}</span></div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {b.service_type && <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{b.service_type}</span>}
                        {b.related_booking_id && (
                          <span data-testid={`return-badge-${b.id}`} title="Prenotazione collegata (andata/ritorno)" className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-mono-num font-bold text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded px-1.5 py-0.5">
                            <Repeat size={9}/> Ritorno
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Avatar url={drv?.photo_url} initials={drv ? initials(drv.first_name, drv.last_name) : "?"} size={36}/>
                        <div className="min-w-0">
                          <div className="text-white text-xs font-bold truncate max-w-[130px]">{drv ? `${drv.first_name} ${drv.last_name}` : "—"}</div>
                          <div className="text-[10px] text-slate-400 truncate max-w-[130px]">{veh ? `${veh.brand} ${veh.model}` : "Veicolo n.d."}</div>
                          {veh?.plate && <div className="text-[10px] font-mono-num text-slate-500 uppercase">{veh.plate}</div>}
                        </div>
                        {veh?.image_url && <img src={veh.image_url} alt="veh" className="w-10 h-10 rounded-md object-cover border border-slate-700 ml-1" onError={(e)=>{e.target.style.display='none';}}/>}
                        {shiftType && <ShiftBadge type={shiftType} bookingId={b.id}/>}
                      </div>
                    </td>
                    <td>
                      <StatusBadge status={b.status}/>
                      {!isDriver && (() => {
                        const ex = exchangesMap[b.id];
                        if (!b.exchange_id || !ex) return null;
                        const META = {
                          pending:   { label: "IN ATTESA",  cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
                          accepted:  { label: "ACCETTATO",  cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
                          rejected:  { label: "RIFIUTATO",  cls: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
                          cancelled: { label: "ANNULLATO",  cls: "bg-slate-500/15 text-slate-300 border-slate-500/40" },
                          completed: { label: "COMPLETATO", cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
                        };
                        const m = META[ex.status] || META.pending;
                        return (
                          <div className="mt-1" data-testid={`exchange-chip-${b.id}`}>
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] font-bold uppercase tracking-wider ${m.cls}`}>
                              <Send size={9}/> Fornitore · {m.label}
                            </span>
                            {ex.to_operator_name && (
                              <div className="text-[10px] text-slate-400 truncate max-w-[160px] mt-0.5">{ex.to_operator_name}</div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="text-right">
                      <div className="mb-1"><PaymentMethodBadge method={b.payment_method} testid={`booking-pay-${b.id}`}/></div>
                      {!isDriver && <div className="text-amber-400 font-mono-num font-bold text-base">{fmtEUR(b.price)}</div>}
                      {!isDriver && <div className={`text-[10px] font-mono-num ${b.margin >= 0 ? "text-emerald-400" : "text-rose-400"}`}>Margine {fmtEUR(b.margin)}</div>}
                      {isDriver && <div className="text-emerald-400 font-mono-num font-bold">{fmtEUR(b.driver_cost)}</div>}
                    </td>
                    <td className="pr-3">
                      <div className="flex justify-end gap-1" onClick={e=>e.stopPropagation()}>
                        <button data-testid={`view-booking-${b.id}`} onClick={()=>setDetailBooking(b)} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-amber-400" title="Visualizza"><Eye size={14}/></button>
                        <button data-testid={`edit-booking-${b.id}`} onClick={()=>{setEditing(b); setShowModal(true);}} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-sky-400" title={isDriver ? "Visualizza" : "Modifica"}><Edit2 size={14}/></button>
                        {!isDriver && <button data-testid={`duplicate-booking-${b.id}`} onClick={()=>duplicate(b)} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-emerald-400" title="Duplica servizio"><Copy size={14}/></button>}
                        {!isDriver && <button data-testid={`return-booking-${b.id}`} onClick={()=>createReturn(b)} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-amber-400" title="Crea ritorno (partenza ↔ destinazione)"><Repeat size={14}/></button>}
                        <a data-testid={`pdf-booking-${b.id}`} href={`${API_BASE}/bookings/${b.id}/pdf`} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-purple-400" title="PDF"><FileText size={14}/></a>
                        {!isDriver && (() => {
                          const ex = exchangesMap[b.id];
                          const active = b.exchange_id && ex && ["pending","accepted"].includes(ex.status);
                          if (active) {
                            return (
                              <span title={`Scambio ${ex.status === "pending" ? "in attesa" : "accettato"} · ${ex.to_operator_name || ""}`}
                                    className="p-1.5 text-emerald-400" data-testid={`booking-has-exchange-${b.id}`}>
                                <ArrowLeftRight size={14}/>
                              </span>
                            );
                          }
                          return (
                            <button data-testid={`exchange-booking-${b.id}`} onClick={()=>setExchangeBooking(b)}
                                    className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-purple-400"
                                    title="Scambia servizio">
                              <ArrowLeftRight size={14}/>
                            </button>
                          );
                        })()}
                        {!isDriver && <button data-testid={`delete-booking-${b.id}`} onClick={()=>remove(b.id)} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-rose-400" title="Elimina"><Trash2 size={14}/></button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* MOBILE CARDS */}
        <div className="md:hidden space-y-3 p-2.5">
          {filteredRows.length === 0 && <div className="p-10 text-center text-slate-500" data-testid="bookings-empty-mobile">{emptyMsg}</div>}
          {filteredRows.map(b => {
            const drv = driver(b.driver_id); const veh = vehicle(b.vehicle_id); const cst = customer(b.customer_id);
            const shiftType = b.driver_id && shiftsMap[`${b.date}|${b.driver_id}`];
            return (
              <div key={b.id} data-testid={`booking-card-${b.id}`} className={`pl-bcard p-4 space-y-3 cursor-pointer ${b.driver_id && b.driver_viewed ? "pl-bcard-viewed" : ""}`} onClick={()=>setDetailBooking(b)}>
                <div className="flex items-start gap-3">
                  <div className="min-w-0">
                    <div className="font-mono-num font-bold text-[#F3EDE0] text-sm break-all" data-testid={`card-number-${b.id}`}>{b.booking_number || "—"}</div>
                    <div className="flex items-center gap-1.5 text-[#A8A39A] text-xs font-mono-num mt-0.5"><Calendar size={13} className="text-[#D6A62A]"/>{fmtDate(b.date)}</div>
                  </div>
                  <div className="ml-auto flex items-center gap-1.5 text-[#FFE08A] font-mono-num font-bold text-lg shrink-0" data-testid={`card-time-${b.id}`}><Clock size={16} className="text-[#D6A62A]"/>{b.time || "--:--"}</div>
                </div>
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {b._received_via_exchange && (
                        <span
                          data-testid={`received-exchange-badge-mobile-${b.id}`}
                          title="Servizio ricevuto da scambio"
                          className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-mono-num font-bold text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded px-1.5 py-0.5"
                        >
                          <ArrowLeftRight size={9}/> Scambio
                        </span>
                      )}
                      {b.driver_id && !isDriver && (
                        b.driver_viewed ? (
                          <span
                            data-testid={`driver-viewed-badge-mobile-${b.id}`}
                            title={`Visualizzato dal driver${b.driver_viewed_at ? ` · ${fmtViewed(b.driver_viewed_at)}` : ""}`}
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-mono-num font-bold text-emerald-300 bg-emerald-500/15 border border-emerald-500/50 rounded px-1.5 py-0.5"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"/> Visualizzato
                          </span>
                        ) : (
                          <span
                            data-testid={`driver-not-viewed-badge-mobile-${b.id}`}
                            title="Il driver non ha ancora aperto il servizio"
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-mono-num font-bold text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded px-1.5 py-0.5"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400"/> Da visualizzare
                          </span>
                        )
                      )}
                    </div>
                  </div>
                  <StatusBadge status={b.status}/>
                </div>
                <div className="flex items-center gap-3 text-[13px] font-semibold uppercase tracking-wide text-[#D6A62A] min-w-0">
                  <span className="truncate">{b.service_type || "Servizio"}</span>
                  {veh && <><span className="w-px h-4 bg-[#D6A62A]/30 shrink-0"/><span className="truncate">{veh.brand} {veh.model}</span></>}
                  <span className="ml-auto flex items-center gap-1 text-[#F3EDE0] font-mono-num shrink-0" title="Passeggeri"><Users size={15} className="text-[#D6A62A]"/>{b.passengers ?? "-"}</span>
                </div>
                <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-2 text-[13px]" data-testid={`card-route-${b.id}`}>
                  <div className="flex gap-1.5 min-w-0 text-[#F3EDE0]"><Flag size={15} className="text-emerald-400 shrink-0 mt-0.5" fill="currentColor"/><span className="line-clamp-2 break-words">{b.pickup || "-"}</span></div>
                  <div className="flex items-center pt-2 text-[#D6A62A]"><span className="w-3 h-px bg-[#D6A62A]/60"/><span className="w-2.5 h-2.5 rounded-full border border-[#D6A62A]"/><span className="w-3 h-px bg-[#D6A62A]/60"/></div>
                  <div className="flex gap-1.5 min-w-0 text-[#F3EDE0]"><Flag size={15} className="text-rose-500 shrink-0 mt-0.5" fill="currentColor"/><span className="line-clamp-2 break-words">{b.destination || "-"}</span></div>
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <UserIcon size={16} className="text-[#D6A62A] shrink-0"/>
                  <span className="text-[#F3EDE0] text-sm font-semibold truncate">{cst?.name || b.passenger_name || "-"}</span>
                  {cst?.type && <span className="text-[10px] uppercase tracking-widest text-[#A8A39A] font-mono-num shrink-0">{cst.type}</span>}
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-[#D6A62A]/15">
                  <Avatar url={drv?.photo_url} initials={drv ? initials(drv.first_name, drv.last_name) : "?"} size={32}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[#F3EDE0] text-xs font-bold truncate">{drv ? `${drv.first_name} ${drv.last_name}` : "—"}</div>
                    <div className="text-[10px] text-[#A8A39A] truncate">{veh ? `${veh.plate}${veh.category ? ` · ${veh.category}` : ""}` : "Veicolo n.d."}{b.luggage ? ` · ${b.luggage} bagagli` : ""}</div>
                  </div>
                  {shiftType && <ShiftBadge type={shiftType} bookingId={b.id}/>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <PaymentMethodBadge method={b.payment_method} compact testid={`booking-pay-m-${b.id}`}/>
                  {!isDriver && Number(b.commission) > 0 && <span className="text-[10px] text-[#A8A39A] font-mono-num">Comm. {fmtEUR(b.commission)}</span>}
                  {!isDriver && <div className="ml-auto text-[#FFE08A] font-mono-num font-bold text-lg" data-testid={`card-price-${b.id}`}>{fmtEUR(b.price)}</div>}
                </div>
                <div className="grid grid-cols-4 gap-2 pt-1" onClick={e=>e.stopPropagation()}>
                  <CardAction testid={`card-view-${b.id}`} icon={Eye} label="Dettaglio" onClick={()=>setDetailBooking(b)}/>
                  <CardAction testid={`card-edit-${b.id}`} icon={Edit2} label={isDriver ? "Apri" : "Modifica"} onClick={()=>{setEditing(b); setShowModal(true);}}/>
                  <CardAction testid={`card-pdf-${b.id}`} icon={FileText} label="PDF" href={`${API_BASE}/bookings/${b.id}/pdf`}/>
                  {!isDriver ? <CardAction testid={`card-duplicate-${b.id}`} icon={Copy} label="Duplica" onClick={()=>duplicate(b)}/>
                    : <CardAction testid={`card-route-btn-${b.id}`} icon={Briefcase} label="Servizio" onClick={()=>setDetailBooking(b)}/>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showModal && <BookingModal booking={editing} onClose={async (s)=>{
        setShowModal(false);
        if (s) {
          // Se stiamo creando un ritorno, colleghiamo anche l'andata al ritorno
          if (returnFromId && s && s.id) {
            try { await api.patch(`/bookings/${returnFromId}`, { related_booking_id: s.id }); } catch (_) {}
          }
          setReturnFromId(null);
          toast.success("Salvata");
          load();
        } else {
          setReturnFromId(null);
        }
      }}/>}

      {/* DETAIL SIDE DRAWER */}
      {/* DETTAGLI PRENOTAZIONE — SLIDE-OVER DESTRO */}
      {detailBooking && (() => {
        const b = detailBooking;
        const drv = driver(b.driver_id); const veh = vehicle(b.vehicle_id); const cst = customer(b.customer_id);
        return (
          <div className="fixed inset-0 z-[55]" data-testid="booking-detail-panel">
            {/* backdrop */}
            <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={()=>setDetailBooking(null)}/>
            {/* panel */}
            <aside className="absolute right-0 top-0 h-full w-full sm:w-[380px] md:w-[420px] lg:w-[440px] bg-[#0B0F17] border-l border-slate-800/80 shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
              {/* HEADER */}
              <div className="px-5 py-4 border-b border-slate-800/80 flex items-start justify-between gap-3 flex-shrink-0">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <StatusBadge status={b.status}/>
                    {b.related_booking_id && (
                      <span data-testid="detail-return-badge" title="Prenotazione collegata (andata/ritorno)" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono-num font-bold text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded px-1.5 py-0.5">
                        <Repeat size={10}/> Andata/Ritorno
                      </span>
                    )}
                  </div>
                  <div className="text-white font-display text-lg font-extrabold tracking-tight">Dettagli Prenotazione</div>
                  <div className="text-amber-400 font-mono-num text-sm mt-0.5">#{b.booking_number}</div>
                </div>
                <button onClick={()=>setDetailBooking(null)} data-testid="close-detail-panel" className="text-slate-400 hover:text-white p-1 -mr-1"><X size={20}/></button>
              </div>

              {/* SCROLLABLE BODY */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
                {/* DATA / ORA */}
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2 text-slate-300"><Calendar size={14} className="text-amber-400"/><span className="font-medium">{fmtDate(b.date)}</span></div>
                  <div className="flex items-center gap-2 text-slate-300"><Clock size={14} className="text-amber-400"/><span className="font-mono-num font-bold">{b.time || "-"}</span></div>
                </div>

                {/* STATO VISUALIZZAZIONE DRIVER (iter 34) */}
                {!isDriver && b.driver_id && (
                  <div
                    data-testid="detail-driver-viewed-status"
                    className={`rounded-lg px-3.5 py-2.5 border ${b.driver_viewed ? "bg-emerald-500/10 border-emerald-500/50" : "bg-amber-500/10 border-amber-500/40"}`}
                  >
                    <div className={`text-[9px] uppercase tracking-widest font-mono-num font-bold mb-1 ${b.driver_viewed ? "text-emerald-300" : "text-amber-300"}`}>
                      Stato Visualizzazione
                    </div>
                    {b.driver_viewed ? (
                      <>
                        <div className="flex items-center gap-2 text-sm text-emerald-200 font-bold">
                          <span className="w-2 h-2 rounded-full bg-emerald-400"/> Visualizzato dal driver
                        </div>
                        {b.driver_viewed_at && (
                          <div className="text-[11px] text-slate-400 font-mono-num mt-1">
                            {fmtViewed(b.driver_viewed_at)}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-amber-200 font-bold">
                        <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"/> Non ancora visualizzato
                      </div>
                    )}
                  </div>
                )}

                {/* CLIENTE */}
                <PanelSection title="Cliente" icon={UserIcon}>
                  <div className="flex items-start gap-3">
                    <Avatar url={cst?.photo_url} initials={cst ? initials(cst.name.split(" ")[0], cst.name.split(" ")[1]) : (b.passenger_name ? initials(b.passenger_name.split(" ")[0], b.passenger_name.split(" ")[1]) : "?")} size={44}/>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-bold truncate">{cst?.name || b.passenger_name || "Cliente occasionale"}</div>
                      {cst?.type && <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mt-0.5">{cst.type}</div>}
                      <div className="mt-2 space-y-1 text-xs">
                        {(cst?.phone || b.phone) && (
                          <a href={`tel:${cst?.phone || b.phone}`} className="flex items-center gap-1.5 text-slate-400 hover:text-amber-400 transition-colors">
                            <Phone size={11}/> {cst?.phone || b.phone}
                          </a>
                        )}
                        {(cst?.email || b.email) && (
                          <a href={`mailto:${cst?.email || b.email}`} className="flex items-center gap-1.5 text-slate-400 hover:text-amber-400 transition-colors truncate">
                            <Mail size={11}/> <span className="truncate">{cst?.email || b.email}</span>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </PanelSection>

                {/* TRATTA */}
                <PanelSection title="Tratta" icon={MapPin}>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2.5">
                      <div className="mt-0.5 flex flex-col items-center">
                        <MapPin size={14} className="text-emerald-400"/>
                        <div className="w-px h-6 bg-slate-700 my-0.5"/>
                        <MapPin size={14} className="text-rose-400"/>
                      </div>
                      <div className="flex-1 min-w-0 space-y-2.5">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Partenza</div>
                          <div className="text-white text-sm truncate">{b.pickup || "-"}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Destinazione</div>
                          <div className="text-white text-sm truncate">{b.destination || "-"}</div>
                        </div>
                      </div>
                    </div>
                    {b.stops && (
                      <div className="pt-1 text-xs text-slate-400">
                        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Fermate: </span>{b.stops}
                      </div>
                    )}
                    {b.service_type && (
                      <div className="pt-1 flex items-center gap-1.5">
                        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Servizio:</span>
                        <span className="text-xs font-bold text-slate-200 bg-slate-800/60 px-2 py-0.5 rounded border border-slate-700">{b.service_type}</span>
                      </div>
                    )}
                  </div>
                </PanelSection>

                {/* ESECUTORE DEL SERVIZIO (autista interno / fornitore esterno / non assegnato) */}
                <ExecutorSection info={resolveExecutor(b, { ex: exchangesMap[b.id], driver: drv, vehicle: veh, supplier: suppliers.find(s => s.id === b.partner_id) })}/>
                <BookingLiveGps bookingId={b.id} hasDriver={!!b.driver_id} />

                {/* DETTAGLI ECONOMICI */}
                {!isDriver && !b._received_via_exchange && (
                  <PanelSection title="Dettagli economici" icon={TrendingUp}>
                    <div className="space-y-2 font-mono-num text-sm">
                      <PanelRow label="Prezzo servizio" value={fmtEUR(b.price)} tone="text-amber-400"/>
                      <PanelRow label="Compenso autista" value={fmtEUR(b.driver_cost)} tone="text-slate-300"/>
                      <PanelRow label="Commissioni" value={fmtEUR(b.commission)} tone="text-slate-300"/>
                      {(b.other_costs || 0) > 0 && <PanelRow label="Altri costi" value={fmtEUR(b.other_costs)} tone="text-slate-400"/>}
                      <div className="border-t border-slate-800/80 my-1.5"/>
                      <div className="flex items-baseline justify-between pt-0.5">
                        <span className="text-slate-300 text-sm font-bold">Margine</span>
                        <span className={`text-lg font-extrabold ${b.margin >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmtEUR(b.margin)}</span>
                      </div>
                    </div>
                  </PanelSection>
                )}

                {/* COMPENSO RICEVENTE (vista fornitore via scambio) — iter 23: solo campo autorizzato */}
                {!isDriver && b._received_via_exchange && (
                  <PanelSection title="Compenso Scambio Servizio" icon={TrendingUp}>
                    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="received-compenso-view">
                      <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-mono-num mb-2">
                        Servizio affidato da {b._sender_name || "collega"} · {b._exchange_number || ""}
                      </div>
                      <div className="flex items-baseline justify-between">
                        <span className="text-slate-300 text-sm font-bold">Compenso per te</span>
                        <span className="text-2xl font-extrabold text-emerald-400 font-mono-num">{fmtEUR(b.price)}</span>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-2">Dati economici del cliente (prezzo cliente, margine, costi) non visibili per privacy del mittente.</div>
                    </div>
                  </PanelSection>
                )}

                {/* NOTE */}
                {b.notes && (
                  <PanelSection title="Note" icon={FileText}>
                    <div className="text-slate-300 text-sm whitespace-pre-wrap leading-relaxed">{b.notes}</div>
                  </PanelSection>
                )}

                {/* SCAMBIO SERVIZI */}
                {!isDriver && (() => {
                  const ex = exchangesMap[b.id];
                  const activeEx = b.exchange_id && ex && ["pending","accepted"].includes(ex.status);
                  if (!activeEx && !ex) return null;
                  const META = {
                    pending:   { label: "In attesa di accettazione",  cls: "border-amber-500/40 bg-amber-500/5 text-amber-300" },
                    accepted:  { label: "Accettato dal fornitore",   cls: "border-emerald-500/40 bg-emerald-500/5 text-emerald-300" },
                    rejected:  { label: "Rifiutato dal fornitore",   cls: "border-rose-500/40 bg-rose-500/5 text-rose-300" },
                    cancelled: { label: "Scambio annullato",         cls: "border-slate-500/40 bg-slate-500/5 text-slate-300" },
                    completed: { label: "Scambio completato",        cls: "border-sky-500/40 bg-sky-500/5 text-sky-300" },
                  };
                  const m = META[ex.status] || META.pending;
                  return (
                    <PanelSection title="Scambio Servizi" icon={ArrowLeftRight}>
                      <div className={`rounded-lg border p-3 ${m.cls}`} data-testid={`detail-exchange-${b.id}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Send size={12}/>
                          <span className="text-[10px] uppercase tracking-widest font-mono-num font-bold">Servizio inviato al fornitore</span>
                        </div>
                        <div className="text-xs space-y-1.5">
                          <div><span className="text-slate-500">Fornitore incaricato: </span><span className="text-white font-bold">{ex.to_operator_name || "-"}</span></div>
                          {ex.partner_name && <div><span className="text-slate-500">Fornitore collegato: </span><span className="text-slate-200">{ex.partner_name}</span></div>}
                          <div><span className="text-slate-500">Stato: </span><span className="font-bold">{m.label.toUpperCase()}</span></div>
                          <div className="text-slate-500">N° scambio: <span className="font-mono-num text-amber-400">{ex.exchange_number || "-"}</span></div>
                          <div className="text-slate-500">Data invio: <span className="text-slate-300">{ex.created_at ? new Date(ex.created_at).toLocaleString("it-IT",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "-"}</span></div>
                          {ex.accepted_at && <div className="text-slate-500">Accettato: <span className="text-emerald-300">{new Date(ex.accepted_at).toLocaleString("it-IT",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"})}</span></div>}
                          {ex.rejected_at && <div className="text-slate-500">Rifiutato: <span className="text-rose-300">{new Date(ex.rejected_at).toLocaleString("it-IT",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"})}</span>{ex.reject_reason ? ` · ${ex.reject_reason}` : ""}</div>}
                          {ex.cancelled_at && <div className="text-slate-500">Annullato: <span className="text-slate-300">{new Date(ex.cancelled_at).toLocaleString("it-IT",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"})}</span></div>}
                        </div>
                        {activeEx && (
                          <div className="mt-3 grid grid-cols-2 gap-2">
                            <Button
                              data-testid={`view-exchange-btn-${b.id}`}
                              onClick={()=>{ setDetailBooking(null); window.location.href = "/scambia-servizi"; }}
                              className="bg-slate-900 border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 font-bold">
                              <ArrowLeftRight size={14}/> Vedi Scambio
                            </Button>
                            <Button
                              data-testid={`cancel-exchange-btn-${b.id}`}
                              onClick={async ()=>{
                                const prompt = ex.status === "accepted"
                                  ? `Annullare lo scambio del servizio?\n\nIl servizio è già stato accettato dal destinatario (${ex.to_operator_name || "collega"}).\nVuoi comunque annullare l'affidamento?\n\nIl destinatario riceverà una notifica di annullamento e il servizio tornerà disponibile.`
                                  : `Annullare lo scambio del servizio?\n\nIl servizio verrà rimosso dall'assegnazione al destinatario e tornerà disponibile per la gestione o per una nuova assegnazione.`;
                                if (!confirm(prompt)) return;
                                try {
                                  await api.post(`/exchanges/${ex.id}/cancel`);
                                  toast.success(ex.status === "accepted" ? "Affidamento annullato" : "Scambio annullato");
                                  load();
                                } catch (e) { toast.error(formatApiError(e)); }
                              }}
                              className="bg-transparent border border-rose-500/40 text-rose-400 hover:bg-rose-500/10 font-bold">
                              <Ban size={14}/> {ex.status === "accepted" ? "Annulla Affidamento" : "Annulla Scambio"}
                            </Button>
                          </div>
                        )}
                        {!activeEx && ["rejected","cancelled"].includes(ex.status) && (
                          <Button
                            data-testid={`reassign-exchange-btn-${b.id}`}
                            onClick={()=>{ setDetailBooking(null); setExchangeBooking(b); }}
                            className="mt-3 w-full bg-amber-500/15 border border-amber-500/40 text-amber-300 hover:bg-amber-500/25 font-bold">
                            <RefreshCw size={14}/> Riassegna Servizio
                          </Button>
                        )}
                      </div>
                    </PanelSection>
                  );
                })()}
              </div>

              {/* ACTIONS FOOTER */}
              <div className="px-5 py-4 border-t border-slate-800/80 flex-shrink-0 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <Button data-testid="panel-edit-btn" onClick={()=>{setEditing(b); setDetailBooking(null); setShowModal(true);}}
                    className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold">
                    <Edit2 size={14}/> Modifica
                  </Button>
                  {!isDriver ? (
                    <Button data-testid="panel-duplicate-btn" onClick={()=>duplicate(b)} variant="outline"
                      className="border-slate-800 bg-transparent text-slate-300 hover:bg-slate-900">
                      <Copy size={14}/> Duplica servizio
                    </Button>
                  ) : (
                    <a href={`${API_BASE}/bookings/${b.id}/pdf`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-slate-800 text-slate-300 hover:bg-slate-900 text-sm font-medium"><FileText size={14}/> PDF</a>
                  )}
                </div>
                {!isDriver && b.driver_id && (
                  <Button data-testid="panel-whatsapp-btn" onClick={()=>setWaBooking(b)}
                    className="w-full bg-[#111] border border-[#25D366]/50 text-[#25D366] hover:bg-[#25D366]/10 font-bold">
                    <MessageCircle size={14}/> Invia tramite WhatsApp
                  </Button>
                )}
                {!isDriver && (
                  <Button data-testid="panel-return-btn" onClick={()=>createReturn(b)} variant="outline"
                    className="w-full border-amber-500/40 bg-amber-500/5 text-amber-300 hover:bg-amber-500/15 font-bold">
                    <Repeat size={14}/> Crea ritorno (inverti partenza ↔ destinazione)
                  </Button>
                )}
                {!isDriver && (
                  <Button data-testid="panel-delete-btn" onClick={()=>remove(b.id)}
                    className="w-full bg-transparent border border-rose-500/40 text-rose-400 hover:bg-rose-500/10 font-bold">
                    <Trash2 size={14}/> Elimina
                  </Button>
                )}
              </div>
            </aside>
          </div>
        );
      })()}

      {/* EXCHANGE DIALOG */}
      {/* MODAL "Invia tramite WhatsApp" — SOLO MANUALE */}
      <WhatsAppModal booking={waBooking} drivers={drivers} onClose={()=>setWaBooking(null)}/>

      <Dialog open={!!exchangeBooking} onOpenChange={(o)=>{ if (!o) { setExchangeBooking(null); setExchangeTargetOperator(""); setExchangeNotes(""); setExchangePartnerId("none"); } }}>
        <DialogContent data-testid="exchange-dialog" className="bg-slate-950 border-slate-800 text-slate-200 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white flex items-center gap-2">
              <ArrowLeftRight size={22} className="text-purple-400"/> Scambia Servizio
            </DialogTitle>
          </DialogHeader>
          {exchangeBooking && (
            <div className="space-y-4 mt-2 text-sm">
              <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-amber-400 font-mono-num font-bold">{exchangeBooking.booking_number}</span>
                  <StatusBadge status={exchangeBooking.status}/>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                  <InfoRow label="Data" value={`${fmtDate(exchangeBooking.date)} · ${exchangeBooking.time || "-"}`}/>
                  <InfoRow label="Cliente" value={exchangeBooking.passenger_name || "-"}/>
                  <InfoRow label="Telefono" value={exchangeBooking.phone || "-"}/>
                  <InfoRow label="Passeggeri" value={`${exchangeBooking.passengers || 1} · Bagagli ${exchangeBooking.luggage || 0}`}/>
                  <InfoRow label="Veicolo" value={(() => { const v = vehicles.find(x=>x.id===exchangeBooking.vehicle_id); return v?`${v.brand} ${v.model} · ${v.plate}`:"-"; })()}/>
                  <InfoRow label="Prezzo" value={<span className="text-amber-400 font-mono-num font-bold">{fmtEUR(exchangeBooking.price)}</span>}/>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-2 text-xs">
                  <InfoRow label="Partenza" value={exchangeBooking.pickup || "-"}/>
                  <InfoRow label="Destinazione" value={exchangeBooking.destination || "-"}/>
                </div>
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Operatore destinatario <span className="text-rose-400">*</span></Label>
                <Select value={exchangeTargetOperator || "none"} onValueChange={(v)=>setExchangeTargetOperator(v==="none"?"":v)}>
                  <SelectTrigger data-testid="exchange-target-operator" className="bg-slate-900 border-slate-800 mt-1"><SelectValue placeholder="Seleziona collega NCC"/></SelectTrigger>
                  <SelectContent className="bg-slate-950 border-slate-800">
                    <SelectItem value="none">-- Seleziona --</SelectItem>
                    {operatorsDir.map(op=><SelectItem key={op.id} value={op.id}>{op.business_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {operatorsDir.length === 0 && (
                  <div className="mt-2 text-xs text-slate-500">Nessun altro operatore disponibile. Chiedi all'admin di crearne uno.</div>
                )}
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Fornitore collegato per Dare/Avere (opzionale)</Label>
                <Select value={exchangePartnerId} onValueChange={setExchangePartnerId}>
                  <SelectTrigger data-testid="exchange-partner-select" className="bg-slate-900 border-slate-800 mt-1"><SelectValue placeholder="Nessuno"/></SelectTrigger>
                  <SelectContent className="bg-slate-950 border-slate-800">
                    <SelectItem value="none">-- Nessuno --</SelectItem>
                    {suppliers.map(s=><SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="mt-1 text-[10px] text-slate-500">All'accettazione del fornitore il costo entra automaticamente nel suo Dare/Avere.</div>
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Note per il collega (opzionale)</Label>
                <textarea data-testid="exchange-notes" rows={2} value={exchangeNotes} onChange={(e)=>setExchangeNotes(e.target.value)}
                  placeholder="Es. servizio già confermato con cliente, richiedi passeggero al gate"
                  className="w-full mt-1 bg-slate-900 border border-slate-800 rounded-md p-2 text-sm text-slate-200"/>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300" onClick={()=>{setExchangeBooking(null); setExchangeTargetOperator(""); setExchangeNotes(""); setExchangePartnerId("none");}}>Annulla</Button>
            <Button data-testid="confirm-exchange-btn"
              disabled={!exchangeTargetOperator}
              onClick={async ()=>{ try {
                await api.post(`/bookings/${exchangeBooking.id}/create-exchange`, {
                  to_operator_id: exchangeTargetOperator,
                  notes: exchangeNotes || null,
                  partner_id: exchangePartnerId !== "none" ? exchangePartnerId : null,
                });
                toast.success("Scambio inviato · notifica al collega");
                setExchangeBooking(null); setExchangeTargetOperator(""); setExchangeNotes(""); setExchangePartnerId("none");
                load();
              } catch (e) { toast.error(formatApiError(e)); } }}
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold disabled:opacity-50">
              <ArrowLeftRight size={14}/> Invia Scambio
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint, tone, pct }) {
  return (
    <div className="pl-panel p-4 flex items-center gap-3">
      <div className="w-11 h-11 rounded-xl border border-[#D6A62A]/40 bg-[#D6A62A]/10 text-[#F1C75B] flex items-center justify-center"><Icon size={20}/></div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider sm:tracking-widest text-slate-500 font-mono-num leading-tight sm:truncate">{label}</div>
        <div className="text-white font-extrabold text-xl font-mono-num">{value}</div>
        {hint && <div className="text-[10px] text-slate-500 truncate">{hint}</div>}
      </div>
      {pct != null && (
        <div className="relative w-11 h-11 flex-shrink-0">
          <svg viewBox="0 0 36 36" className="w-11 h-11 -rotate-90">
            <circle cx="18" cy="18" r="15" fill="none" stroke="#1e293b" strokeWidth="3"/>
            <circle cx="18" cy="18" r="15" fill="none" stroke="currentColor" className="text-amber-400" strokeWidth="3" strokeDasharray={`${pct*0.94} 100`} strokeLinecap="round"/>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-amber-400 font-mono-num">{pct}%</div>
        </div>
      )}
    </div>
  );
}
function CardAction({ icon: Icon, label, onClick, href, testid }) {
  const cls = "pl-action flex flex-col items-center justify-center gap-0.5 h-14 rounded-xl";
  const inner = <><Icon size={18}/><span className="text-[10px] tracking-wide">{label}</span></>;
  if (href) return <a href={href} target="_blank" rel="noreferrer" data-testid={testid} className={cls}>{inner}</a>;
  return <button type="button" onClick={onClick} data-testid={testid} className={cls}>{inner}</button>;
}
function FilterField({ label, children }) { return (<div><Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{label}</Label>{children}</div>); }
function Avatar({ url, initials, size = 40 }) {
  if (url) return <img src={url} alt="" style={{ width: size, height: size }} className="rounded-full object-cover border border-slate-700 flex-shrink-0" onError={(e)=>{e.target.style.display='none';}}/>;
  return <div style={{ width: size, height: size, fontSize: size * 0.35 }} className="rounded-full bg-gradient-to-br from-amber-500/40 to-amber-700/40 border border-amber-500/30 flex items-center justify-center text-amber-200 font-extrabold flex-shrink-0">{initials}</div>;
}
function ShiftBadge({ type, bookingId }) {
  const map = { REST: { cls: "bg-rose-500/20 text-rose-300 border-rose-500/40", label: "Riposo", icon: <AlertTriangle size={10}/> }, NIGHT: { cls: "bg-sky-500/20 text-sky-300 border-sky-500/40", label: "Notte" }, DAY: { cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", label: "Giorno" } };
  const m = map[type]; if (!m) return null;
  return <span data-testid={type === "REST" ? `rest-warn-${bookingId}` : undefined} className={`ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${m.cls} text-[10px] font-bold uppercase tracking-widest border`}>{m.icon}{m.label}</span>;
}
function InfoBlock({ label, children }) { return (<div className="rounded-lg border border-slate-800 bg-slate-950/50 p-2.5"><div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{label}</div>{children}</div>); }
function Section({ title, children }) { return (<div className="mt-4"><div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-2">{title}</div><div className="rounded-xl border border-slate-800 bg-slate-950/40 p-4">{children}</div></div>); }
function PanelSection({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500 font-mono-num mb-2 font-bold">
        {Icon && <Icon size={11}/>} {title}
      </div>
      <div className="rounded-xl border border-slate-800/80 bg-slate-900/40 p-3.5">{children}</div>
    </div>
  );
}
function PanelRow({ label, value, tone }) { return (<div className="flex items-baseline justify-between"><span className="text-slate-400 text-xs">{label}</span><span className={`${tone || "text-slate-200"} font-mono-num`}>{value}</span></div>); }
function Row({ label, value, tone, big }) { return (<div className="flex justify-between items-baseline"><span className="text-slate-400 text-xs">{label}</span><span className={`${tone || "text-slate-200"} ${big?"text-base":""}`}>{value}</span></div>); }
function InfoRow({ label, value }) { return (<div><div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{label}</div><div className="text-slate-200 truncate">{value}</div></div>); }


// =====================================================================
// WhatsAppModal — Invio MANUALE del servizio tramite WhatsApp
// Nessuna API, nessun invio automatico. Apre wa.me con testo precompilato.
// =====================================================================
function WhatsAppModal({ booking, drivers, onClose }) {
  if (!booking) return null;
  const drv = (drivers || []).find(d => d.id === booking.driver_id) || null;
  const drvName = drv ? `${drv.first_name || ""} ${drv.last_name || ""}`.trim() : "";
  const drvPhone = (drv && drv.phone) ? String(drv.phone).trim() : "";
  const hasPhone = !!drvPhone;
  const phoneDigits = drvPhone.replace(/\D/g, ""); // wa.me vuole solo cifre
  const fmtDate = (iso) => {
    if (!iso) return "";
    try { const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; } catch { return iso; }
  };
  // Testo driver-safe: nessun prezzo, margine, compenso, dare/avere
  const lines = ["🚘 STRIANO NCC ROMA", "NUOVO SERVIZIO ASSEGNATO", ""];
  lines.push(`Ciao${drvName ? ` ${drvName.split(" ")[0]}` : ""},`);
  lines.push("ti è stato assegnato il seguente servizio:");
  lines.push("");
  if (booking.booking_number) lines.push(`📋 Numero servizio: ${booking.booking_number}`);
  if (booking.date) lines.push(`📅 Data: ${fmtDate(booking.date)}`);
  if (booking.time) lines.push(`🕘 Ora: ${booking.time}`);
  if (booking.service_type) lines.push(`🚘 Servizio: ${booking.service_type}`);
  if (booking.pickup) lines.push(`📍 Partenza: ${booking.pickup}`);
  if (booking.destination) lines.push(`📍 Destinazione: ${booking.destination}`);
  if (booking.passenger_name) lines.push(`👤 Cliente: ${booking.passenger_name}`);
  if (booking.passengers != null) lines.push(`👥 Passeggeri: ${booking.passengers}`);
  if (booking.luggage != null) lines.push(`🧳 Bagagli: ${booking.luggage}`);
  if (booking.flight_number) lines.push(`✈️ Volo: ${booking.flight_number}`);
  if (booking.notes) lines.push(`📝 Note: ${booking.notes}`);
  lines.push("");
  lines.push("Puoi verificare tutti i dettagli del servizio direttamente dal gestionale.");
  lines.push("");
  lines.push("Buon lavoro,");
  lines.push("STRIANO NCC ROMA");
  const message = lines.join("\n");
  const waUrl = hasPhone ? `https://wa.me/${phoneDigits}?text=${encodeURIComponent(message)}` : "";
  return (
    <Dialog open={!!booking} onOpenChange={(o)=>{ if (!o) onClose(); }}>
      <DialogContent data-testid="whatsapp-dialog" className="bg-slate-950 border-slate-800 text-slate-200 max-w-xl">
        <DialogHeader>
          <DialogTitle data-testid="whatsapp-dialog-title" className="text-amber-400 flex items-center gap-2">
            <MessageCircle size={18}/> Invia servizio tramite WhatsApp
          </DialogTitle>
          <div className="text-xs text-slate-500 mt-1">Verifica i dati prima di aprire WhatsApp.</div>
        </DialogHeader>
        {!hasPhone ? (
          <div className="space-y-3" data-testid="whatsapp-no-number">
            <div className="rounded-xl border border-rose-500/40 bg-rose-500/5 p-4">
              <div className="flex items-center gap-2 text-rose-300 text-sm font-bold uppercase tracking-widest">
                <AlertCircle size={16}/> Numero WhatsApp non disponibile
              </div>
              <div className="text-sm text-slate-300 mt-2 leading-relaxed">
                Questo autista non ha un numero di telefono configurato. Inserisci il numero nella scheda autista per poter utilizzare l'invio tramite WhatsApp.
              </div>
            </div>
            <div className="flex justify-end">
              <Button data-testid="whatsapp-close-btn" onClick={onClose} variant="outline" className="border-slate-800 bg-transparent text-slate-300">Chiudi</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Autista */}
            <div className="rounded-xl border border-slate-800/80 bg-slate-900/40 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-2 font-bold">Autista assegnato</div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500/40 to-amber-700/40 border border-amber-500/30 flex items-center justify-center text-amber-200 font-extrabold text-sm">
                  {(drvName || "?").split(" ").map(s=>s[0]||"").join("").slice(0,2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div data-testid="whatsapp-driver-name" className="text-white font-bold">{drvName || "—"}</div>
                  <div data-testid="whatsapp-driver-phone" className="text-xs text-slate-400 font-mono-num">📞 {drvPhone}</div>
                </div>
              </div>
            </div>
            {/* Preview */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-2 font-bold">Anteprima messaggio</div>
              <textarea
                data-testid="whatsapp-message-preview"
                readOnly
                value={message}
                rows={14}
                className="w-full bg-slate-900 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 font-mono-num leading-relaxed"
              />
              <div className="mt-1 text-[10px] text-slate-500">Il messaggio è precompilato — l'invio finale sarà fatto da te dentro WhatsApp.</div>
            </div>
            {/* Actions */}
            <div className="flex justify-end gap-2">
              <Button data-testid="whatsapp-cancel-btn" onClick={onClose} variant="outline" className="border-slate-800 bg-transparent text-slate-300">Annulla</Button>
              <a
                data-testid="whatsapp-open-btn"
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={()=>setTimeout(onClose, 100)}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-[#25D366] hover:bg-[#1ebe5c] text-white font-bold shadow-[0_0_20px_rgba(37,211,102,0.25)]">
                <MessageCircle size={14}/> Apri WhatsApp
              </a>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
